import { ref, watch } from "vue";
import { Task } from "@/types/v2";
import { useRouter } from "vue-router";
import { useTasksStore } from "@/stores/v2";
import { storeToRefs } from "pinia";
import { TasksStorageService } from "@/services/v2/tasks/storage";
import { debounce } from "@/utils/v2";

export function useTask() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask, tasks } = storeToRefs(useTasksStore());

  // ===================================================================
  // Constants
  // ===================================================================
  const TASK_OBJECTIVES = {
    FISH_TRANSFER: "fish_transfer" as const,
    VACCINATION: "vaccination" as const,
    ENVIRONMENTAL: "environmental" as const,
    MORTALITY: "mortality" as const,
    FISH_RECONCILIATION: "fish_reconciliation" as const,
    PRE_CHALLENGE: "pre_challenge" as const,
    CHALLENGE: "challenge" as const,
    FISH_IMPORT: "fish_import" as const
  };

  // ===================================================================
  // State
  // ===================================================================
  const router = useRouter();
  const selectedTask = ref<Task>();
  const showStartTaskModal = ref<boolean>(false);

  // ===================================================================
  // Actions
  // ===================================================================
  // Start Task
  const handleStartTask = (task: Task): void => {
    // Get objective id safely - handle both object and string formats
    const objectiveId = task.objective?.id || task.objective;
    
    // Route directly to specific task types to avoid the LoadTask router
    if (objectiveId === "environmental") {
      router.push({ name: "EnvironmentalCheck", params: { key: task.key } });
    } else if (objectiveId === "vaccination") {
      router.push({ name: "VaccineConfigurations", params: { key: task.key } });
    } else if (objectiveId === "challenge") {
      router.push({ name: "ChallengeConfigurations", params: { key: task.key } });
    } else if (objectiveId === "mortality") {
      router.push({ name: "MortalityCheckOverview", params: { key: task.key } });
    } else if (objectiveId === "fish_reconciliation") {
      router.push({ name: "FishReconciliationOverview", params: { key: task.key } });
    } else if (objectiveId === "pre_challenge") {
      router.push({ name: "PreChallenge", params: { key: task.key } });
    } else if (objectiveId === "fish_transfer") {
      router.push({ name: "AnimalTransfer", params: { key: task.key } });
    } else if (objectiveId === "fish_import") {
      router.push({ name: "FishImportImports", params: { key: task.key } });
    } else if (objectiveId === "environmental_modifier") {
      router.push({ name: "EnvironmentalModifier", params: { key: task.key } });
    } else {
      // Fallback to LoadTask for unknown task types
      console.warn("Unknown or missing task objective:", objectiveId, "for task:", task);
      router.push({ name: "LoadTask", params: { key: task.key } });
    }
  };

  // Select Task
  const handleSelectTask = (task: Task): void => {
    useTasksStore().setSelectedTask(task);
    selectedTask.value = task;
    showStartTaskModal.value = !showStartTaskModal.value;
  };

  // ===================================================================
  // Miscellaneous
  // ===================================================================
  // Task Status Text
  const taskStatusText = (task: Task): string => {
    if (!task.startedBy) return "";
    const action = task.status === "finished" ? "Completed" : "Started";
    return `${action} by ${task.startedBy}`;
  };

  // ===================================================================
  // Mutations
  // ===================================================================
  // Start Observer for changes & store in index DB
  const startObserver = (): void => {
    watch(() => activeTask.value, (task) => {
      if (task) {
        updateActiveTaskInStorage(task);
      }
    }, {deep: true})
  }

  // ===================================================================
  // Setters
  // ===================================================================
  // Store Task in IndexDB
  const updateActiveTaskInStorage = debounce(async (task: Task) => {
    if (task) {
      return TasksStorageService.storeActiveTask(task);
    }
  }, 500)

  return {
    // ===================================================================
    // CONSTANTS
    // ===================================================================
    TASK_OBJECTIVES,

    // ===================================================================
    // State
    // ===================================================================
    tasks,
    activeTask,
    selectedTask,
    showStartTaskModal,

    // ===================================================================
    // Actions
    // ===================================================================
    startObserver,
    taskStatusText,
    handleStartTask,
    handleSelectTask,

  };
}
